import { parseFormula } from "./parser";
// Row/table-returning table functions whose result columns are those of their
// first (table) argument. Lets member access validate `fn(table).col`.
const ROW_OR_TABLE_FNS = new Set([
    "first",
    "last",
    "nth",
    "get",
    "find",
    "findlast",
    "maxrow",
    "minrow",
    "medianrow",
    "where",
    "resample",
]);
export function resolveIdentifiers(ast, context) {
    const errors = [];
    const resultType = inferType(ast, context, errors, null);
    // A formula must reduce to a single number. Flag the statically-known
    // non-numeric result types at edit time. `unknown` (and `number`) never
    // fire, so there are no false positives on cases we can't trace.
    const resultError = resultTypeError(resultType);
    if (resultError) {
        errors.push({ ...resultError, path: "", range: rangeOf(ast) });
    }
    return errors.length === 0 ? { ok: true } : { ok: false, errors };
}
function resultTypeError(resultType) {
    switch (resultType) {
        case "boolean":
            return {
                kind: "BooleanResult",
                message: "A formula must produce a number, but this is a true/false condition.",
            };
        case "array":
            return {
                kind: "ArrayResult",
                message: "This evaluates to a list of values, not a single number. Reduce it (avg, sum, last, …) or pick a row first.",
            };
        case "table":
            return {
                kind: "TableResult",
                message: "This evaluates to a whole table. Pick a row — e.g. last(…), find(…) — and read a column off it.",
            };
        case "object":
            return {
                kind: "ObjectResult",
                message: "This evaluates to a row, not a number. Read a column off it with `.column`.",
            };
        case "string":
            return {
                kind: "StringResult",
                message: "This evaluates to text, not a number. Reference a numeric column instead.",
            };
        default:
            return null;
    }
}
function rangeOf(node) {
    switch (node.type) {
        case "VariableAccess":
            return node.range;
        case "FunctionCall":
            return node.nameRange;
        case "MemberAccess":
            return node.range;
        case "Comparison":
        case "Logical":
        case "Not":
            return node.range;
        default:
            return undefined;
    }
}
function inferType(node, ctx, errors, scope) {
    switch (node.type) {
        case "Literal":
            return typeof node.value === "string" ? "string" : "number";
        case "VariableAccess":
            return inferVariableAccess(node, ctx, errors, scope);
        case "FunctionCall":
            return inferFunctionCall(node, ctx, errors, scope);
        case "MemberAccess":
            return inferMemberAccess(node, ctx, errors, scope);
        case "BinaryOperation": {
            requireNumeric(node.left, ctx, errors, scope);
            requireNumeric(node.right, ctx, errors, scope);
            return "number";
        }
        case "UnaryOperation": {
            requireNumeric(node.operand, ctx, errors, scope);
            return "number";
        }
        case "Comparison": {
            checkComparison(node, ctx, errors, scope);
            return "boolean";
        }
        case "Logical": {
            requireBoolean(node.left, ctx, errors, scope);
            requireBoolean(node.right, ctx, errors, scope);
            return "boolean";
        }
        case "Not": {
            requireBoolean(node.operand, ctx, errors, scope);
            return "boolean";
        }
    }
}
function requireNumeric(node, ctx, errors, scope, context = "arithmetic") {
    requireNumericType(inferType(node, ctx, errors, scope), node, errors, context);
}
/**
 * Numbers compare with every operator; text only with text, and only for
 * (in)equality — organism == "TC". `unknown` sides are never flagged.
 */
function checkComparison(node, ctx, errors, scope) {
    const left = inferType(node.left, ctx, errors, scope);
    const right = inferType(node.right, ctx, errors, scope);
    if (left !== "string" && right !== "string") {
        requireNumericType(left, node.left, errors, "comparison");
        requireNumericType(right, node.right, errors, "comparison");
        return;
    }
    const otherSide = left === "string" ? right : left;
    if (otherSide !== "string" && otherSide !== "unknown") {
        errors.push({
            kind: "NonNumericOperand",
            path: "",
            message: `Text can only be compared with text, but found a "${otherSide}".`,
            range: node.range,
        });
        return;
    }
    if (node.operator !== "==" && node.operator !== "!=") {
        errors.push({
            kind: "NonNumericOperand",
            path: "",
            message: `Text can only be compared with == or !=, not "${node.operator}".`,
            range: node.range,
        });
    }
}
function requireNumericType(t, node, errors, context) {
    if (t === "number" || t === "unknown")
        return;
    errors.push({
        kind: "NonNumericOperand",
        path: "",
        message: context === "comparison"
            ? `Comparison expects numeric operands, but found a "${t}".`
            : `Arithmetic expects numeric operands, but found a "${t}".`,
        range: rangeOf(node),
    });
}
function requireBoolean(node, ctx, errors, scope) {
    const t = inferType(node, ctx, errors, scope);
    if (t === "boolean" || t === "unknown")
        return;
    errors.push({
        kind: "NonBooleanOperand",
        path: "",
        message: `'and'/'or'/'not' expect conditions, but found a "${t}".`,
        range: rangeOf(node),
    });
}
function inferVariableAccess(node, ctx, errors, scope) {
    const path = pathOf(node);
    const { identifier, properties, range } = node;
    // Inside a predicate / column_ref argument, a bare identifier binds to the
    // table's columns first (column scope wins on collisions).
    if (scope && properties.length === 0) {
        const col = scope.columns.find((c) => c.code === identifier);
        if (col)
            return col.kind === "string" ? "string" : "number";
    }
    if (identifier === "upstream") {
        resolveUpstream(path, properties, range, ctx, errors);
        return "number";
    }
    if (identifier === "curves" && ctx.scopes.curves) {
        resolveCurves(path, properties, range, ctx.scopes.curves, errors);
        return "number";
    }
    if (identifier === "row" || identifier === "prev") {
        resolveRow(identifier, path, properties, range, ctx, errors);
        const col = ctx.scopes.tabular?.columns.find((c) => c.code === properties[0]);
        return col?.kind === "string" ? "string" : "number";
    }
    if (identifier === "elapsed") {
        resolveElapsed(path, properties, range, ctx, errors);
        return "number";
    }
    // The `samples.` prefix was the old two-level grammar. It is gone; the
    // message says where the data moved rather than reporting an unknown local.
    if (identifier === "samples") {
        errors.push({
            kind: "UnknownLocal",
            path,
            token: identifier,
            message: "sample data is addressed as <abbr>.<test>.<attr> — drop the 'samples.' prefix",
            range,
        });
        return "unknown";
    }
    const sampleRef = ctx.scopes.samples?.find((s) => s.abbreviation === identifier);
    if (sampleRef) {
        return resolveSamplePath(sampleRef, path, properties, range, errors);
    }
    if (ctx.selfCode && identifier === ctx.selfCode) {
        errors.push({
            kind: "SelfReference",
            path,
            token: identifier,
            message: `'${identifier}' cannot reference its own value`,
            range,
        });
        return "unknown";
    }
    const localRef = ctx.scopes.local.find((r) => r.code === identifier);
    if (localRef) {
        const isInvRef = localRef.kind === "INVENTORY_ITEM_REFERENCE";
        if (properties.length === 0) {
            if (localRef.kind === "TABULAR_DATA")
                return "table";
            // A bare inventory reference is an object — the result-type check then
            // tells the author to read a property off it.
            if (isInvRef)
                return "object";
            return "number";
        }
        // Property access on an inventory reference → a numeric property.
        if (isInvRef) {
            if (properties.length > 1) {
                errors.push({
                    kind: "UnexpectedProperty",
                    path,
                    token: properties[1],
                    message: `Unexpected property '${properties[1]}' after '${properties[0]}'`,
                    range,
                });
            }
            const props = localRef.referenceProperties;
            if (props && !props.some((p) => p.code === properties[0])) {
                errors.push({
                    kind: "UnknownColumn",
                    path,
                    token: properties[0],
                    message: `'${properties[0]}' is not a property of '${identifier}'`,
                    range,
                });
            }
            return "number";
        }
        // Property access on a local.
        if (localRef.kind !== "TABULAR_DATA") {
            errors.push({
                kind: "UnexpectedProperty",
                path,
                token: properties[0],
                message: `Unexpected property '${properties[0]}' on '${identifier}'`,
                range,
            });
            return "unknown";
        }
        if (properties.length > 1) {
            errors.push({
                kind: "UnexpectedProperty",
                path,
                token: properties[1],
                message: `Unexpected property '${properties[1]}' after column name`,
                range,
            });
        }
        // `table.column` is a column projection → array. Validate the column when
        // the table's columns are known.
        validateColumn(localRef.tabularColumns, properties[0], path, range, errors);
        return "array";
    }
    // Not found. Bias the message by context.
    if (scope) {
        errors.push({
            kind: "UnknownColumn",
            path,
            token: identifier,
            message: `'${identifier}' is not a column on this table`,
            range,
        });
        return "unknown";
    }
    errors.push({
        kind: "UnknownLocal",
        path,
        token: identifier,
        message: `'${identifier}' is not a local attribute on this template`,
        range,
    });
    return "unknown";
}
function inferFunctionCall(node, ctx, errors, scope) {
    const sigs = ctx.functions.filter((f) => f.name === node.functionName);
    if (sigs.length === 0) {
        errors.push({
            kind: "UnknownFunction",
            path: node.functionName,
            token: node.functionName,
            message: `Unknown function '${node.functionName}'`,
            range: node.nameRange,
        });
    }
    // Columns of the first (table) argument, for predicate/column_ref slots.
    const argColumns = node.arguments.length > 0 ? tableColumnsOf(node.arguments[0], ctx) : null;
    const argTypes = node.arguments.map((arg, i) => {
        const slotType = paramTypeAt(sigs, i);
        const lazySlot = slotType === "predicate" || slotType === "column_ref";
        const argScope = lazySlot && argColumns ? { columns: argColumns } : scope;
        const t = inferType(arg, ctx, errors, argScope);
        if (slotType === "predicate" && t !== "boolean" && t !== "unknown") {
            errors.push({
                kind: "NonBooleanCondition",
                path: "",
                message: `'${node.functionName}' expects a condition here (e.g. \`temp > 30\`), but found a "${t}".`,
                range: rangeOf(arg),
            });
        }
        if (slotType === "column_ref" &&
            t !== "number" &&
            t !== "unknown") {
            errors.push({
                kind: "NonNumericColumn",
                path: "",
                message: `'${node.functionName}' expects a numeric column/expression here, but found a "${t}".`,
                range: rangeOf(arg),
            });
        }
        return t;
    });
    if (sigs.length === 0)
        return "unknown";
    const sig = pickSignature(sigs, argTypes) ?? sigs[0];
    return mapReturnType(sig.returnType);
}
function inferMemberAccess(node, ctx, errors, scope) {
    const objType = inferType(node.object, ctx, errors, scope);
    const cols = tableColumnsOf(node.object, ctx);
    if (objType === "object") {
        // Reading a column off a row → scalar.
        const col = validateColumn(cols, node.property, `.${node.property}`, node.propertyRange ?? node.range, errors);
        return col && col.kind === "string" ? "string" : "number";
    }
    if (objType === "table") {
        // Column projection off a table → array.
        validateColumn(cols, node.property, `.${node.property}`, node.propertyRange ?? node.range, errors);
        return "array";
    }
    if (objType === "unknown")
        return "unknown";
    errors.push({
        kind: "PropertyAccessOnNonObject",
        path: `.${node.property}`,
        token: node.property,
        message: `Cannot read '.${node.property}' off a "${objType}".`,
        range: node.propertyRange ?? node.range,
    });
    return "unknown";
}
/**
 * Validate a column name against a known column list. Returns the column ref
 * when found; pushes an UnknownColumn error when the list is known but lacks
 * it. When `columns` is undefined (table identity not statically known) it
 * degrades to no-op (validity is checked at eval time).
 */
function validateColumn(columns, columnCode, path, range, errors) {
    if (columnCode === "_index")
        return undefined;
    if (!columns)
        return undefined;
    const col = columns.find((c) => c.code === columnCode);
    if (!col) {
        errors.push({
            kind: "UnknownColumn",
            path,
            token: columnCode,
            message: `'${columnCode}' is not a column on this table`,
            range,
        });
    }
    return col;
}
/** Columns of the table denoted by a node, when statically known. */
function tableColumnsOf(node, ctx) {
    if (node.type === "VariableAccess" && node.properties.length === 0) {
        const local = ctx.scopes.local.find((r) => r.code === node.identifier);
        if (local && local.kind === "TABULAR_DATA") {
            return local.tabularColumns ?? null;
        }
        return null;
    }
    if (node.type === "FunctionCall" && ROW_OR_TABLE_FNS.has(node.functionName)) {
        return node.arguments.length > 0
            ? tableColumnsOf(node.arguments[0], ctx)
            : null;
    }
    return null;
}
/** The declared param type at position `i` across overloads (first hit). */
function paramTypeAt(sigs, i) {
    for (const sig of sigs) {
        const params = sig.parameters;
        const last = params[params.length - 1];
        const param = i < params.length ? params[i] : last?.variadic ? last : undefined;
        if (param) {
            const t = param.type;
            return typeof t === "string" ? t : t[0];
        }
    }
    return undefined;
}
function mapReturnType(returnType) {
    switch (returnType) {
        case "scalar":
            return "number";
        case "boolean":
            return "boolean";
        case "array":
            return "array";
        case "object":
            return "object";
        case "table":
            return "table";
        default:
            return "unknown";
    }
}
/** Pick the overload whose parameter types accept the inferred arg types. */
function pickSignature(sigs, argTypes) {
    for (const sig of sigs) {
        if (signatureAccepts(sig, argTypes))
            return sig;
    }
    return null;
}
function signatureAccepts(sig, argTypes) {
    const params = sig.parameters;
    const last = params[params.length - 1];
    const variadic = !!last?.variadic;
    if (!variadic) {
        const required = params.filter((p) => !p.optional).length;
        if (argTypes.length < required || argTypes.length > params.length) {
            return false;
        }
    }
    else if (argTypes.length < params.length - (last.optional ? 1 : 0)) {
        return false;
    }
    for (let i = 0; i < argTypes.length; i++) {
        const param = i < params.length ? params[i] : variadic ? last : undefined;
        if (!param)
            return false;
        if (!paramTypeMatches(param.type, argTypes[i]))
            return false;
    }
    return true;
}
function paramTypeMatches(expected, actual) {
    if (actual === "unknown")
        return true;
    const exp = typeof expected === "string" ? [expected] : expected;
    if (exp.includes("any"))
        return true;
    // Lazy slots take a raw expression of any inferred type.
    if (exp.includes("predicate") || exp.includes("column_ref"))
        return true;
    const mapped = actual === "number" || actual === "string" ? "scalar" : actual;
    return exp.includes(mapped);
}
function pathOf(node) {
    return node.properties.length === 0
        ? node.identifier
        : `${node.identifier}.${node.properties.join(".")}`;
}
/** The numbers a curve offers to formulas. */
export const CURVE_FORMULA_FIELDS = [
    "slope",
    "intercept",
    "r_squared",
    "rt_min",
    "rt_max",
];
function resolveCurves(path, properties, range, curves, errors) {
    const [code, field, ...rest] = properties;
    if (!code || !field) {
        errors.push({
            kind: "IncompletePath",
            path,
            message: "Use curves.<compound>.<field>, e.g. curves.putrescine.slope",
            range,
        });
        return;
    }
    if (!curves.some((c) => c.code === code)) {
        errors.push({
            kind: "UnknownLocal",
            path,
            token: code,
            message: `'${code}' has no calibration curve for this template`,
            range,
        });
        return;
    }
    if (!CURVE_FORMULA_FIELDS.includes(field) || rest.length > 0) {
        errors.push({
            kind: "UnexpectedProperty",
            path,
            token: field,
            message: `A curve offers ${CURVE_FORMULA_FIELDS.join(", ")}`,
            range,
        });
    }
}
function resolveUpstream(path, properties, range, ctx, errors) {
    if (!ctx.scopes.upstream) {
        errors.push({
            kind: "ScopeNotAvailable",
            path,
            message: "Upstream references are not available in this context",
            range,
        });
        return;
    }
    if (properties.length === 0) {
        errors.push({
            kind: "IncompletePath",
            path,
            message: "'upstream' must be followed by a code (e.g. upstream.ph)",
            range,
        });
        return;
    }
    if (properties.length > 1) {
        errors.push({
            kind: "UnexpectedProperty",
            path,
            token: properties[1],
            message: `Unexpected property '${properties[1]}' after upstream code`,
            range,
        });
        return;
    }
    const code = properties[0];
    if (!ctx.scopes.upstream.some((r) => r.code === code)) {
        errors.push({
            kind: "UnknownUpstream",
            path,
            token: code,
            message: `'${code}' is not a declared upstream dependency`,
            range,
        });
    }
}
function resolveRow(identifier, path, properties, range, ctx, errors) {
    if (!ctx.scopes.tabular) {
        errors.push({
            kind: "ScopeNotAvailable",
            path,
            message: `'${identifier}' references are only available in tabular computed columns`,
            range,
        });
        return;
    }
    if (properties.length === 0) {
        errors.push({
            kind: "IncompletePath",
            path,
            message: `'${identifier}' must be followed by a column name (e.g. ${identifier}.value)`,
            range,
        });
        return;
    }
    if (properties.length > 1) {
        errors.push({
            kind: "UnexpectedProperty",
            path,
            token: properties[1],
            message: `Unexpected property '${properties[1]}' after ${identifier} column`,
            range,
        });
        return;
    }
    const col = properties[0];
    const colRef = ctx.scopes.tabular.columns.find((c) => c.code === col);
    if (!colRef) {
        errors.push({
            kind: "UnknownRowColumn",
            path,
            token: col,
            message: `'${col}' is not a column on this tabular attribute`,
            range,
        });
        return;
    }
}
function resolveElapsed(path, properties, range, ctx, errors) {
    if (!ctx.scopes.tabular) {
        errors.push({
            kind: "ScopeNotAvailable",
            path,
            message: "'elapsed' is only available in tabular computed columns",
            range,
        });
        return;
    }
    if (!ctx.scopes.tabular.index) {
        errors.push({
            kind: "ScopeNotAvailable",
            path,
            message: "'elapsed' requires the table to have an index column to measure against",
            range,
        });
        return;
    }
    if (properties.length > 0) {
        errors.push({
            kind: "UnexpectedProperty",
            path,
            token: properties[0],
            message: `'elapsed' is a duration value, not an object — remove '.${properties[0]}'`,
            range,
        });
    }
}
/**
 * Resolve `<abbr>.<testCode>.<attrCode>` — the one sample grammar.
 *
 * The sample abbreviation is a top-level identifier holding an object of test
 * codes, each an object of attribute codes, each an array of replicate values
 * across the sample's test instances. That is exactly the variable shape
 * HarvestFormulaService.buildSampleVariablesByAbbreviation builds at runtime,
 * and the same one HarvestKpiService uses.
 *
 * The test level is part of the path rather than flattened away: two test
 * templates on the same sample may legitimately declare the same analyte code
 * — an oven dry matter and a microwave dry matter, or an internal histamine
 * method beside an external lab's — and the earlier flat grammar had to forbid
 * that outright to stay unambiguous.
 *
 * A partial path is an object rather than an error only when it is a prefix of
 * something valid, so the editor can complete through it.
 */
export function resolveSamplePath(sample, path, properties, range, errors) {
    if (properties.length === 0) {
        errors.push({
            kind: "IncompletePath",
            path,
            message: `Sample '${sample.abbreviation}' must be followed by a test code and an attribute code`,
            range,
        });
        return "object";
    }
    const testCode = properties[0];
    const test = sample.tests.find((t) => t.code === testCode);
    if (!test) {
        errors.push({
            kind: "UnknownSampleTest",
            path,
            token: testCode,
            message: `Sample '${sample.abbreviation}' has no test '${testCode}'`,
            range,
        });
        return "unknown";
    }
    if (properties.length < 2) {
        errors.push({
            kind: "IncompletePath",
            path,
            message: `Test '${testCode}' must be followed by an attribute code`,
            range,
        });
        return "object";
    }
    if (properties.length > 2) {
        errors.push({
            kind: "UnexpectedProperty",
            path,
            token: properties[2],
            message: `Unexpected property '${properties[2]}'`,
            range,
        });
        return "unknown";
    }
    const attrCode = properties[1];
    if (!test.attrs.some((a) => a.code === attrCode)) {
        errors.push({
            kind: "UnknownSampleTestAttr",
            path,
            token: attrCode,
            message: `Test '${testCode}' on sample '${sample.abbreviation}' has no attribute '${attrCode}'`,
            range,
        });
        return "unknown";
    }
    // One value per replicate test instance.
    return "array";
}
export function parseAndResolve(formula, context) {
    let parsed;
    try {
        parsed = parseFormula(formula);
    }
    catch (err) {
        return { ok: false, phase: "parse", ...describeParseFailure(formula, err) };
    }
    const res = resolveIdentifiers(parsed.ast, context);
    if (res.ok)
        return { ok: true, parsed };
    return { ok: false, phase: "resolve", errors: res.errors };
}
/**
 * Turns a raw parser throw into a classified, human-readable failure. The split
 * hinges on where the parser gave up: out of input (`<END-OF-FILE>`, or a
 * position at/after the end) means the formula is merely *incomplete* — the
 * everyday mid-typing state — whereas a leftover/unexpected token mid-stream is
 * *malformed*. Incomplete failures get a targeted hint from the trailing token.
 */
function describeParseFailure(formula, err) {
    const raw = err instanceof Error ? err.message : String(err);
    const pos = err && typeof err === "object" && "pos" in err
        ? err.pos
        : undefined;
    const index = typeof pos?.index === "number" ? pos.index : undefined;
    const trimmed = formula.trimEnd();
    const hitEof = raw.includes("<END-OF-FILE>") || (index != null && index >= trimmed.length);
    if (hitEof) {
        return { kind: "incomplete", message: incompleteHint(trimmed), index };
    }
    const token = index != null ? tokenAt(formula, index) : undefined;
    return {
        kind: "malformed",
        message: token ? `Unexpected \`${token}\`.` : "Check the formula syntax.",
        index,
    };
}
/** A targeted "what's missing" hint for an incomplete formula, from its tail. */
function incompleteHint(text) {
    const base = "Expression is incomplete";
    if (!text)
        return `${base}.`;
    // A dangling open paren anywhere takes priority over the trailing token.
    let depth = 0;
    for (const ch of text) {
        if (ch === "(")
            depth++;
        else if (ch === ")")
            depth = Math.max(0, depth - 1);
    }
    if (depth > 0)
        return `${base} — close the open parenthesis.`;
    const last = text[text.length - 1];
    if (last === ".")
        return `${base} — name a column or field after the \`.\`.`;
    if (last === ",")
        return `${base} — add the next argument.`;
    const symbolOp = text.match(/(==|!=|>=|<=|[-+*/<>])\s*$/);
    if (symbolOp)
        return `${base} — add the right-hand side of \`${symbolOp[1]}\`.`;
    const wordOp = text.match(/\b(and|or|not)\s*$/);
    if (wordOp)
        return `${base} — add the right-hand side of \`${wordOp[1]}\`.`;
    return `${base}.`;
}
/** The contiguous token starting at an offset, for an "Unexpected `x`" message. */
function tokenAt(formula, index) {
    if (index >= formula.length)
        return undefined;
    const rest = formula.slice(index);
    const m = rest.match(/^\s*([A-Za-z_]\w*|==|!=|>=|<=|[-+*/().,<>]|\d+(?:\.\d+)?)/);
    if (m)
        return m[1];
    const word = rest.trim().split(/\s+/)[0];
    return word || undefined;
}
//# sourceMappingURL=context.js.map